import { useState } from 'react';
import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Contract, ContractTemplate } from '@shared/types';
import { useClients } from '../../hooks/useClients';
import { clientService } from '../../services/clientService';
import { AISummaryDialog } from './AISummaryDialog';

interface DetailsStepProps {
  data: Partial<Contract>;
  onUpdate: (updates: Partial<Contract>) => void;
  template?: ContractTemplate;
  onClientSelect?: (harvestClientId?: string) => void;
  onImportFromHarvest?: () => void;
}

export function DetailsStep({ data, onUpdate, template, onClientSelect, onImportFromHarvest }: DetailsStepProps) {
  const { clients, loading, reload } = useClients();
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);

  // Find selected client for displaying details
  const selectedClient = clients.find(c => c.name === data.clientName);
  
  // Check if selected client is from Harvest
  const canImportFromHarvest = selectedClient?.externalSource === 'harvest' && selectedClient?.externalId;

  const handleOpenAIDialog = () => {
    if (!data.title || !data.clientName) {
      toast.error('Please enter contract title and select a client first');
      return;
    }
    setShowAIDialog(true);
  };

  const handleSummaryGenerated = (summary: string) => {
    onUpdate({ executiveSummary: summary });
  };

  const handleQuickAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientName.trim()) return;

    setCreatingClient(true);
    const toastId = toast.loading('Creating client...');

    try {
      const newClient = await clientService.createClient({
        name: newClientName,
        status: 'active',
      });
      toast.success('Client created!', { id: toastId });
      setNewClientName('');
      setShowAddClient(false);
      await reload();
      // Auto-select the newly created client
      onUpdate({ clientName: newClient.name });
    } catch (error) {
      console.error('Failed to create client:', error);
      toast.error('Failed to create client.', { id: toastId });
    } finally {
      setCreatingClient(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Contract Details</h2>
      
      {template?.hasScope && (
        <div>
          <label htmlFor="projectTitle" className="block text-sm font-medium text-gray-700 mb-2">
            Project Name *
          </label>
          <input
            type="text"
            id="projectTitle"
            value={data.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder="e.g., SharePoint Intranet Implementation"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="off"
            required
          />
          <p className="mt-1 text-sm text-gray-500">Give this project a descriptive name</p>
        </div>
      )}

      <div>
        <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
          Client *
        </label>
        
        {showAddClient ? (
          <form onSubmit={handleQuickAddClient} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Enter client name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
                disabled={creatingClient}
              />
              <button
                type="submit"
                disabled={creatingClient || !newClientName.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {creatingClient ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddClient(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={creatingClient}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            <select
              id="clientName"
              value={data.clientName || ''}
              onChange={(e) => {
                const clientName = e.target.value;
                onUpdate({ clientName });
                
                // Find the selected client and notify parent of Harvest ID
                const client = clients.find(c => c.name === clientName);
                if (onClientSelect) {
                  onClientSelect(client?.externalId);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={loading}
            >
              <option value="">Select a client...</option>
              {clients
                .filter(c => c.status === 'active' || c.status === 'prospect')
                .map((client) => (
                  <option key={client.id} value={client.name}>
                    {client.name}
                    {client.city && client.state && ` • ${client.city}, ${client.state}`}
                  </option>
                ))}
            </select>
            
            <button
              type="button"
              onClick={() => setShowAddClient(true)}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add new client
            </button>
          </div>
        )}

        {/* Show selected client details */}
        {selectedClient && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Client Information</h4>
              {canImportFromHarvest && onImportFromHarvest && template?.hasScope && (
                <button
                  type="button"
                  onClick={onImportFromHarvest}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm0 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10z"/>
                    <circle cx="12" cy="8" r="2"/>
                    <path d="M12 11c-3 0-5 2-5 4v2h10v-2c0-2-2-4-5-4z"/>
                  </svg>
                  Import Estimate from Harvest
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {selectedClient.addressLine1 && (
                <div>
                  <span className="text-gray-500">Address:</span>
                  <p className="text-gray-900">
                    {selectedClient.addressLine1}
                    {selectedClient.addressLine2 && `, ${selectedClient.addressLine2}`}
                  </p>
                  {selectedClient.city && (
                    <p className="text-gray-900">
                      {selectedClient.city}, {selectedClient.state} {selectedClient.zip}
                    </p>
                  )}
                </div>
              )}
              {selectedClient.industry && (
                <div>
                  <span className="text-gray-500">Industry:</span>
                  <p className="text-gray-900">{selectedClient.industry}</p>
                </div>
              )}
              {selectedClient.website && (
                <div>
                  <span className="text-gray-500">Website:</span>
                  <p className="text-gray-900">
                    <a 
                      href={selectedClient.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {selectedClient.website}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {template?.hasScope && (
        <>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="executiveSummary" className="block text-sm font-medium text-gray-700">
                Executive Summary *
              </label>
              <button
                type="button"
                onClick={handleOpenAIDialog}
                disabled={!data.title || !data.clientName}
                className="inline-flex items-center px-3 py-1.5 border border-purple-300 text-xs font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="w-4 h-4 mr-1" />
                Generate with AI
              </button>
            </div>
            <textarea
              id="executiveSummary"
              value={data.executiveSummary || ''}
              onChange={(e) => onUpdate({ executiveSummary: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Provide a high-level overview of the contract..."
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Click "Generate with AI" to automatically create a professional summary, or write your own.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="estimatedStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Start Date
              </label>
              <input
                type="date"
                id="estimatedStartDate"
                value={data.estimatedStartDate || ''}
                onChange={(e) => onUpdate({ estimatedStartDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label htmlFor="projectDuration" className="block text-sm font-medium text-gray-700 mb-2">
                Project Duration
              </label>
              <input
                type="text"
                id="projectDuration"
                value={data.projectDuration || ''}
                onChange={(e) => onUpdate({ projectDuration: e.target.value })}
                placeholder="e.g., 8 to 10 weeks"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </>
      )}

      <div>
        <label htmlFor="expirationDate" className="block text-sm font-medium text-gray-700 mb-2">
          Expiration Date *
        </label>
        <input
          type="date"
          id="expirationDate"
          value={data.expirationDate || ''}
          onChange={(e) => onUpdate({ expirationDate: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          required
        />
      </div>

      {/* AI Summary Generator Dialog */}
      <AISummaryDialog
        isOpen={showAIDialog}
        onClose={() => setShowAIDialog(false)}
        clientName={data.clientName || ''}
        projectName={data.title || ''}
        onGenerated={handleSummaryGenerated}
      />
    </div>
  );
}

